/**
 * MyWork Component
 *
 * Unified "My Work" section at top of the main app.
 * Combines:
 * - Pinned Projects (bookmarked quotes for quick access)
 * - Active Sessions (currently running jobs with live timers)
 *
 * Features:
 * - Collapsible section
 * - Compact cards optimized for iPad
 * - Click to navigate to project
 * - Quick actions on active sessions (pause/resume/complete)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { usePinnedQuotes } from "./MyPinnedQuotes";

export default function MyWork({
  allQuotes = [],
  activeSessions = {},
  onSelectQuote,
  onNavigateToJob,
  onPauseJob,
  onResumeJob,
  onCompleteJob,
  pendingTicketsReady = 0,
  onAssignPendingTickets,
  reworkQueue = [],
  onStartReworkTask,
}) {
  const { pinnedQuotes, unpinQuote } = usePinnedQuotes();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [tick, setTick] = useState(0);

  // Tick for live timer updates
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Enrich pinned quotes with full quote data
  const enrichedPinned = useMemo(() => {
    if (pinnedQuotes.length === 0 || allQuotes.length === 0) return [];

    return pinnedQuotes
      .map((pinned) => {
        const fullQuote = allQuotes.find(
          (q) => q.quoteNumber?.toString() === pinned.quoteNumber
        );
        return fullQuote ? { ...pinned, quote: fullQuote } : null;
      })
      .filter(Boolean);
  }, [pinnedQuotes, allQuotes]);

  // Group active sessions - by sessionGroupId if they share one, otherwise show individually
  const activeByProject = useMemo(() => {
    const groups = {};
    const individualSessions = [];

    Object.entries(activeSessions).forEach(([jobId, session]) => {
      if (!session || (session.state !== "running" && session.state !== "paused")) {
        return;
      }

      const sessionWithId = { jobId, ...session };

      // If session has a sessionGroupId, group with others that share it
      if (session.sessionGroupId) {
        const key = `group-${session.sessionGroupId}`;
        if (!groups[key]) {
          groups[key] = {
            quoteNumber: session.quoteNumber,
            orderNumber: session.orderNumber,
            projectName: session.projectName,
            sessionGroupId: session.sessionGroupId,
            isGroupSession: true,
            sessions: [],
          };
        }
        groups[key].sessions.push(sessionWithId);
      } else {
        // Individual session - track separately
        individualSessions.push(sessionWithId);
      }
    });

    // Convert groups to array
    const result = Object.values(groups);

    // Add individual sessions as their own "group" of 1
    individualSessions.forEach((session) => {
      result.push({
        quoteNumber: session.quoteNumber,
        orderNumber: session.orderNumber,
        projectName: session.projectName,
        sessionGroupId: null,
        isGroupSession: false,
        sessions: [session],
      });
    });

    return result;
  }, [activeSessions]);

  // Format elapsed time
  const formatElapsed = useCallback((session) => {
    if (!session) return "0:00";

    // Get accumulated time from previous pause/resume cycles (floor to remove decimals)
    const accumulated = Math.floor(session.accumulatedSeconds || 0);
    let seconds = 0;

    if (session.state === "running" && session.startTime) {
      const startMs = new Date(session.startTime).getTime();
      const currentSegment = Math.floor((Date.now() - startMs) / 1000);
      // Total time = accumulated from previous segments + current segment
      seconds = accumulated + currentSegment;
    } else if (session.durationSeconds) {
      // When paused, durationSeconds should already include accumulated time
      seconds = Math.floor(session.durationSeconds);
    } else {
      seconds = accumulated;
    }

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  }, []);

  // Don't render if nothing to show
  const hasContent = enrichedPinned.length > 0 || activeByProject.length > 0 || reworkQueue.length > 0;
  if (!hasContent) return null;

  // Force re-render on tick for live timers
  void tick;

  const totalActive = Object.values(activeSessions).filter(
    (s) => s?.state === "running" || s?.state === "paused"
  ).length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      {/* Header */}
      <div
        className="px-4 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-200 transition-colors"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-purple-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
            <span className="text-sm font-semibold text-gray-900">My Work</span>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {enrichedPinned.length > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                {enrichedPinned.length} pinned
              </span>
            )}
            {totalActive > 0 && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                {totalActive} active
              </span>
            )}
            {reworkQueue.length > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                {reworkQueue.length} reprint{reworkQueue.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-gray-500 transition-transform ${
            isCollapsed ? "" : "rotate-180"
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100">
          {/* Pending Tickets Ready for Assignment Banner */}
          {pendingTicketsReady > 0 && (
            <div
              className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={onAssignPendingTickets}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-amber-800">
                    {pendingTicketsReady} ticket{pendingTicketsReady > 1 ? "s" : ""} ready for assignment
                  </div>
                  <div className="text-xs text-amber-600">
                    These quotes now have orders. Click to assign your time to jobs.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-amber-700">
                <span className="text-sm font-medium">Assign Now</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          )}

          {/* Rework Tasks Section */}
          {reworkQueue.length > 0 && (
            <div className="mb-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Reprints Needed
              </div>
              <div className="flex flex-wrap gap-2">
                {reworkQueue.map((task) => (
                  <ReworkTaskCard
                    key={task.reworkRequiredId || task.eventId}
                    task={task}
                    onClick={() => onStartReworkTask?.(task)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-4">
            {/* LEFT: Pinned Projects */}
            {enrichedPinned.length > 0 && (
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 text-amber-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                  </svg>
                  Pinned Projects
                </div>
                <div className="flex flex-wrap gap-2">
                  {enrichedPinned.map(({ quoteNumber, quote }) => (
                    <PinnedCard
                      key={quoteNumber}
                      quoteNumber={quoteNumber}
                      quote={quote}
                      onSelect={() => onSelectQuote?.(quote)}
                      onUnpin={() => unpinQuote(quoteNumber)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* RIGHT: Active Sessions */}
            {activeByProject.length > 0 && (
              <div className={`${enrichedPinned.length > 0 ? "lg:w-[400px] lg:flex-shrink-0" : "flex-1"}`}>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Active Sessions
                </div>
                <div className="flex flex-col gap-2">
                  {activeByProject.map((project, idx) => (
                    <ActiveProjectCard
                      key={project.sessionGroupId || project.sessions[0]?.jobId || `project-${idx}`}
                      project={project}
                      formatElapsed={formatElapsed}
                      onNavigate={onNavigateToJob}
                      onPause={onPauseJob}
                      onResume={onResumeJob}
                      onComplete={onCompleteJob}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact card for pinned projects
 */
function PinnedCard({ quoteNumber, quote, onSelect, onUnpin }) {
  const shipDate = quote?.user_Date1;
  const shipDateColor = getShipDateColor(shipDate);
  const shipDateLabel = formatShipDate(shipDate);

  return (
    <div
      className="group flex items-center gap-2 px-2.5 py-1.5 rounded-md border bg-slate-200 border-gray-200 hover:border-amber-400 hover:bg-amber-50 transition-all cursor-pointer min-w-0"
      onClick={onSelect}
    >
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-amber-600 font-bold">#{quoteNumber}</span>
          {shipDateLabel && (
            <span className={`text-[10px] ${shipDateColor}`}>{shipDateLabel}</span>
          )}
        </div>
        <span
          className="text-[11px] text-gray-600 truncate max-w-[120px]"
          title={quote?.user_Text4}
        >
          {quote?.user_Text4 || "No project name"}
        </span>
      </div>

      {/* Unpin button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onUnpin();
        }}
        className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-500 hover:text-red-500 rounded hover:bg-white transition-all flex-shrink-0"
        title="Unpin"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * Card for active project sessions
 */
function ActiveProjectCard({
  project,
  formatElapsed,
  onNavigate,
  onPause,
  onResume,
  onComplete,
}) {
  const { quoteNumber, orderNumber, projectName, sessions, isGroupSession } = project;
  const hasRunning = sessions.some((s) => s.state === "running");
  const hasPaused = sessions.some((s) => s.state === "paused");

  // For group sessions (multi-start), show bulk actions in header
  // For individual sessions, show individual controls inline with each session
  const showBulkActions = isGroupSession && sessions.length > 1;

  return (
    <div
      className={`rounded-lg border p-2.5 transition-all ${
        hasRunning
          ? "bg-green-50 border-green-200"
          : "bg-amber-50 border-amber-200"
      }`}
    >
      {/* Project Header */}
      <div className="flex items-center justify-between mb-2">
        <div
          className="flex items-center gap-2 cursor-pointer hover:opacity-80"
          onClick={() => {
            if (sessions[0]) {
              onNavigate?.({
                quoteNumber,
                orderNumber,
                jobId: sessions[0].jobId,
              });
            }
          }}
        >
          {quoteNumber && (
            <span className="text-xs text-purple-600 font-medium">Q{quoteNumber}</span>
          )}
          {orderNumber && (
            <span className="text-xs text-gray-500">#{orderNumber}</span>
          )}
          {projectName && (
            <span className="text-xs text-gray-700 truncate max-w-[150px]" title={projectName}>
              {projectName}
            </span>
          )}
          {isGroupSession && (
            <span className="text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
              Group
            </span>
          )}
        </div>

        {/* Bulk Actions - only for group sessions */}
        {showBulkActions && (
          <div className="flex items-center gap-1">
            {hasRunning && (
              <button
                onClick={() => sessions.forEach((s) => onPause?.(sessionToJob(s)))}
                className="p-1 bg-amber-100 hover:bg-amber-200 rounded text-amber-700 transition-colors"
                title="Pause all"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
            {hasPaused && !hasRunning && (
              <button
                onClick={() => sessions.forEach((s) => onResume?.(sessionToJob(s)))}
                className="p-1 bg-green-100 hover:bg-green-200 rounded text-green-700 transition-colors"
                title="Resume all"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
            <button
              onClick={() => sessions.forEach((s) => onComplete?.(sessionToJob(s)))}
              className="p-1 bg-red-100 hover:bg-red-200 rounded text-red-600 transition-colors"
              title="Complete all"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Session List */}
      <div className="flex flex-col gap-1.5">
        {sessions.map((session) => (
          <div
            key={session.jobId}
            className={`flex items-center justify-between px-2 py-1.5 rounded text-xs ${
              session.state === "running"
                ? "bg-green-100 text-green-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {/* Session Info */}
            <div className="flex items-center gap-2">
              <span className="font-mono font-medium">{session.jobNumber || session.jobId}</span>
              <span className="text-gray-500">|</span>
              <span className="text-gray-600">{session.operationCode || "—"}</span>
              <span className="text-gray-500">|</span>
              <span className="font-mono tabular-nums font-medium">
                {session.state === "running" && (
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block mr-1 animate-pulse" />
                )}
                {formatElapsed(session)}
              </span>
            </div>

            {/* Individual Session Actions - only for non-group sessions */}
            {!showBulkActions && (
              <div className="flex items-center gap-1 ml-2">
                {session.state === "running" ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPause?.(sessionToJob(session));
                    }}
                    className="p-1 bg-amber-200 hover:bg-amber-300 rounded text-amber-700 transition-colors"
                    title="Pause"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onResume?.(sessionToJob(session));
                    }}
                    className="p-1 bg-green-200 hover:bg-green-300 rounded text-green-700 transition-colors"
                    title="Resume"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete?.(sessionToJob(session));
                  }}
                  className="p-1 bg-red-100 hover:bg-red-200 rounded text-red-600 transition-colors"
                  title="Complete"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper: format ship date
function formatShipDate(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

// Helper: Convert session to job-like object for handlers
function sessionToJob(session) {
  return {
    id: session.jobId,
    jobNumber: session.jobNumber,
    name: session.projectName || session.jobName,
    productCode: session.productCode,
    qty: session.qty,
    orderNumber: session.orderNumber,
    quoteNumber: session.quoteNumber,
  };
}

// Helper: get ship date color based on urgency
function getShipDateColor(dateStr) {
  if (!dateStr) return "text-gray-500";
  try {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "text-red-600";
    if (diffDays === 0) return "text-green-600";
    if (diffDays <= 3) return "text-orange-600";
    if (diffDays <= 7) return "text-amber-600";
    return "text-gray-500";
  } catch {
    return "text-gray-500";
  }
}

/**
 * Card for rework/reprint tasks
 */
function ReworkTaskCard({ task, onClick }) {
  const severityColor = task.severity === 'scrap'
    ? 'border-red-400 bg-red-50'
    : 'border-amber-400 bg-amber-50';

  const severityBadge = task.severity === 'scrap'
    ? 'bg-red-100 text-red-700'
    : 'bg-amber-100 text-amber-700';

  return (
    <div
      className={`group flex flex-col gap-1 px-3 py-2 rounded-lg border-2 ${severityColor} hover:shadow-md transition-all cursor-pointer min-w-[200px] max-w-[280px]`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-gray-800 truncate">
          {task.jobNumber}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${severityBadge}`}>
          {task.severity === 'scrap' ? 'SCRAP' : 'REPRINT'}
        </span>
      </div>

      {task.projectName && (
        <span className="text-[11px] text-gray-600 truncate" title={task.projectName}>
          {task.projectName}
        </span>
      )}

      <div className="flex items-center gap-2 text-[10px] text-gray-500">
        {task.issueLabel && (
          <span className="truncate">{task.issueLabel}</span>
        )}
        {task.ageHours && (
          <span className="text-gray-400">• {task.ageHours}h ago</span>
        )}
      </div>

      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-gray-400">
          From: {task.discoveredAt || task.reportedBy}
        </span>
        <button
          className="px-2 py-0.5 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-medium rounded transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
        >
          Start Reprint
        </button>
      </div>
    </div>
  );
}
